import {Text as PixiText, utils, TextMetrics} from 'pixi.js';
import {AUTOMATION_META, AutomationMeta} from '@gongxigames/framework/core/fragments/Autotest';

interface AscentPercentMap {
    [fontFName: string]: number;
}

/**
 * @deprecated Use RichText instead
 */
export class Text extends PixiText {

    private static _ASCENT_PERCENT_MAP: AscentPercentMap = {};

    private _maxWidth?: number;

    public static addAscentPercent(fontName: string, ascentPercent: number): void {
        this._ASCENT_PERCENT_MAP[fontName] = ascentPercent;
    }

    /**
     * this is override of parent method. Here we add scale to match in max width borders and shift position
     * @param respectDirty
     */
    public updateText(respectDirty: boolean): void {
        // @ts-ignore
        const style = this._style;

        // check if style has changed..
        // @ts-ignore
        if (this.localStyleID !== style.styleID) {
            // @ts-ignore
            this.dirty = true;
            // @ts-ignore
            this.localStyleID = style.styleID;
        }

        // @ts-ignore
        if (!this.dirty && respectDirty) {
            return;
        }
        // @ts-ignore
        this._font = this._style.toFontString();
        const context = this.context;
        // @ts-ignore
        const measured = TextMetrics.measureText(this._text || ' ', this._style, this._style.wordWrap, this.canvas);
        let width = measured.width;
        const scale = this._maxWidth ? Math.min(this._maxWidth / width, 1) : 1;

        width *= scale;

        const height = measured.height;
        const lines = measured.lines;
        const lineHeight = measured.lineHeight;
        const lineWidths = measured.lineWidths.map((n: number) => n * scale);
        const maxLineWidth = measured.maxLineWidth * scale;
        const fontProperties = measured.fontProperties;

        this.canvas.width = Math.ceil((Math.max(1, width) + (style.padding * 2)) * this._resolution);
        this.canvas.height = Math.ceil((Math.max(1, height) + (style.padding * 2)) * this._resolution);

        const scaleS = 1 - scale;
        const halfOfStroke = style.strokeThickness / 2;
        const ascent = fontProperties.ascent * this.getAscentPercent();
        const delta = lineHeight * scaleS * 0.5 ;

        context.scale(this._resolution * scale, this._resolution * scale);
        context.clearRect(0, 0, this.canvas.width, this.canvas.height);
        // @ts-ignore
        context.font = this._font;
        context.lineWidth = style.strokeThickness;
        context.textBaseline = style.textBaseline;
        context.lineJoin = style.lineJoin;
        context.miterLimit = style.miterLimit;
        let linePositionX;
        let linePositionY;

        // require 2 passes if a shadow; the first to draw the drop shadow, the second to draw the text
        const passesCount = style.dropShadow ? 2 : 1;

        // For v4, we drew text at the colours of the drop shadow underneath the normal text. This gave the correct zIndex,
        // but features such as alpha and shadowblur did not look right at all, since we were using actual text as a shadow.
        //
        // For v5.0.0, we moved over to just use the canvas API for drop shadows, which made them look much nicer and more
        // visually please, but now because the stroke is drawn and then the fill, drop shadows would appear on both the fill
        // and the stroke; and fill drop shadows would appear over the top of the stroke.
        //
        // For v5.1.1, the new route is to revert to v4 style of drawing text first to get the drop shadows underneath normal
        // text, but instead drawing text in the correct location, we'll draw it off screen (-paddingY), and then adjust the
        // drop shadow so only that appears on screen (+paddingY). Now we'll have the correct draw order of the shadow
        // beneath the text, whilst also having the proper text shadow styling.
        for (let i = 0; i < passesCount; ++i) {
            const isShadowPass = style.dropShadow && i === 0;
            const dsOffsetText = isShadowPass ? height * 2 : 0; // we only want the drop shadow, so put text way off-screen
            const dsOffsetShadow = dsOffsetText * this.resolution;

            if (isShadowPass) {
                // On Safari, text with gradient and drop shadows together do not position correctly
                // if the scale of the canvas is not 1: https://bugs.webkit.org/show_bug.cgi?id=197689
                // Therefore we'll set the styles to be a plain black whilst generating this drop shadow
                context.fillStyle = 'black';
                context.strokeStyle = 'black';
                const dropShadowColor = style.dropShadowColor;
                const rgb = utils.hex2rgb(typeof dropShadowColor === 'number'
                    // @ts-ignore
                    ? dropShadowColor : utils.string2hex(dropShadowColor));

                context.shadowColor = 'rgba(' + rgb[0] * 255 + ',' + rgb[1] * 255 + ',' + rgb[2] * 255 + ',' + style.dropShadowAlpha + ')';
                context.shadowBlur = style.dropShadowBlur;
                context.shadowOffsetX = Math.cos(style.dropShadowAngle) * style.dropShadowDistance;
                context.shadowOffsetY = (Math.sin(style.dropShadowAngle) * style.dropShadowDistance) + dsOffsetShadow;
            } else {
                // set canvas text styles
                // @ts-ignore
                context.fillStyle = this._generateFillStyle(style, lines, measured);
                // TODO: Can't have different types for getter and setter. The getter shouldn't have the number type as
                //       the setter converts to string. See this thread for more details:
                //       https://github.com/microsoft/TypeScript/issues/2521
                // @ts-ignore
                context.strokeStyle = style.stroke;
                context.shadowColor = '0';
                context.shadowBlur = 0;
                context.shadowOffsetX = 0;
                context.shadowOffsetY = 0;
            }
            // draw lines line by line
            for (let i1 = 0; i1 < lines.length; i1++) {
                linePositionX = style.strokeThickness / 2;
                linePositionY = halfOfStroke + ascent + i1 * lineHeight + delta / scale;

                if (style.align === 'right') {
                    linePositionX += maxLineWidth - lineWidths[i1];
                } else if (style.align === 'center') {
                    linePositionX += (maxLineWidth - lineWidths[i1]) / 2;
                }

                if (style.stroke && style.strokeThickness) {
                    // @ts-ignore
                    this.drawLetterSpacing(lines[i1], linePositionX + style.padding, linePositionY + style.padding - dsOffsetText, true);
                }

                if (style.fill) {
                    // @ts-ignore
                    this.drawLetterSpacing(lines[i1], linePositionX + style.padding, linePositionY + style.padding - dsOffsetText);
                }
            }
        }

        // @ts-ignore
        this.updateTexture();
    }

    public get maxWidth(): number | undefined {
        return this._maxWidth;
    }

    public set maxWidth(value: number | undefined) {
        this._maxWidth = value;
        // @ts-ignore
        this.dirty = true;
    }

    private getAscentPercent(): number {
        // @ts-ignore
        const fontFamily = (this._style.fontFamily instanceof Array) ? this._style.fontFamily[0] : this._style.fontFamily;

        return Text._ASCENT_PERCENT_MAP[fontFamily] ? Text._ASCENT_PERCENT_MAP[fontFamily] : 1;
    }

    [AUTOMATION_META](): AutomationMeta {
        return {
            type: 'text',
            text: this.text
        };
    }
}